import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { api } from '../lib/api';
import { CheckCircle2, Loader2 } from 'lucide-react';

/**
 * Stripe Express returns the partner here on onboarding completion. We:
 *   1. Refresh the account status from Stripe (this also persists the
 *      mirrored flags onto the user row).
 *   2. Bounce them back into the partner dashboard with a success state.
 *
 * The /refresh URL Stripe calls if the link expired just returns to /partners,
 * and the dashboard's existing onboarding button mints a fresh link.
 */
export default function PartnerOnboardingComplete() {
  const nav = useNavigate();
  const loc = useLocation();
  const [stage, setStage] = useState('checking'); // checking | done | error

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.get('/stripe/connect/status');
      } catch (_e) {
        if (!cancelled) setStage('error');
        return;
      }
      if (cancelled) return;
      setStage('done');
      setTimeout(() => {
        if (!cancelled) nav('/partners', { replace: true });
      }, 1500);
    })();
    return () => { cancelled = true; };
  }, [loc.search, nav]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="ml-container py-24 text-center" data-testid="onboarding-complete-page">
        {stage === 'checking' && (
          <>
            <Loader2 className="mx-auto text-[hsl(var(--primary)/0.6)] animate-spin" size={36}/>
            <p className="mt-4 text-[hsl(var(--primary)/0.7)]">Finishing Stripe onboarding…</p>
          </>
        )}
        {stage === 'done' && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-600" size={48}/>
            <h1 className="font-serif text-3xl text-[hsl(var(--primary))] mt-4">All set.</h1>
            <p className="text-[hsl(var(--primary)/0.7)] mt-2">Taking you back to your dashboard…</p>
          </>
        )}
        {stage === 'error' && (
          <>
            <h1 className="font-serif text-3xl text-[hsl(var(--primary))] mt-4">Hmm — we couldn't read your status</h1>
            <p className="text-[hsl(var(--primary)/0.7)] mt-2">No worries. Head back to your dashboard and try refreshing from there.</p>
            <Link to="/partners" className="ml-btn-primary mt-6 inline-flex" data-testid="onboarding-complete-back">
              Back to dashboard
            </Link>
          </>
        )}
      </main>
    </div>
  );
}
